/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, HStack, Skeleton, VStack } from '@chakra-ui/react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React, { useState, ReactNode } from 'react';
import { BookCard } from './BookCard';
import { InfoBox } from '../atoms/InfoBox';

interface HorizontalBooksProps {
  name: string;
  description: string;
  hookApi: any;
  showMore?: () => void;
  params: any;
  hideIfEmpty?: boolean;
  receivedBook?: boolean;
  bestSellerName?: string;
  borderColor?: string;
  bg?: string;
  showEditorialCode?: boolean;
}

interface SliderComponentProps {
  children: (props: { currentSlide: number }) => ReactNode;
  name: string;
  description: string;
  showMore?: () => void;
  size?: number;
  borderColor?: string;
  bg?: string;
}

export const SliderComponent: React.FC<SliderComponentProps> = ({
  size = 1,
  children,
  name,
  description,
  borderColor,
  bg,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const sliderSettings = (slidesToShow: number, centerPadding: string) => ({
    infinite: slidesToShow === 1 && size > 1,
    centerMode: slidesToShow === 1,
    centerPadding,
    slidesToShow,
    initialSlide: 0,
    speed: 500,
    beforeChange: (_: number, next: number) => setCurrentSlide(next),
  });

  return (
    <VStack width="100%" spacing={3} align="flex-start">
      <InfoBox title={name} description={description} borderColor={borderColor} bg={bg} />
      <Box width="100%" paddingLeft="5%" paddingRight={{ base: '0%', md: '5%' }}>
        <Box display={{ base: 'none', md: 'block' }} overflow="hidden" width="100%">
          <Slider {...sliderSettings(6, '80px')}>{children({ currentSlide })}</Slider>
        </Box>
        <Box display={{ base: 'block', md: 'none' }} overflow="hidden" width="100%">
          <Slider {...sliderSettings(1, '90px')}>{children({ currentSlide })}</Slider>
        </Box>
      </Box>
    </VStack>
  );
};

const BookCardSkeleton: React.FC = () => (
  <Box
    width={{ base: '85%', md: '90%' }}
    margin="auto"
    padding={{ base: '40px 0px', md: '30px 0px' }}
  >
    <VStack
      borderRadius="10px"
      align="flex-start"
      backgroundColor="white"
      width="100%"
      border="1px solid #ededed"
      padding="8px"
      boxShadow="rgba(100, 100, 111, 0.2) 0px 5px 8px 0px"
    >
      <Skeleton borderRadius="5px" width="100%" sx={{ aspectRatio: '0.7' }} />
      <Skeleton height="12px" width="80%" mt="8px" borderRadius="4px" />
      <Skeleton height="12px" width="60%" borderRadius="4px" />
    </VStack>
  </Box>
);

const HorizontalBooksSkeleton: React.FC<{ name: string; description: string; borderColor?: string; bg?: string }> = ({ name, description, borderColor, bg }) => (
  <VStack width="100%" spacing={3} align="flex-start">
    <InfoBox title={name} description={description} borderColor={borderColor} bg={bg} />
    <Box width="100%" paddingLeft="5%" paddingRight={{ base: '0%', md: '5%' }}>
      <Box display={{ base: 'none', md: 'flex' }} overflow="hidden" width="100%">
        <HStack spacing={0} width="100%">
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} flex="0 0 16.66%">
              <BookCardSkeleton />
            </Box>
          ))}
        </HStack>
      </Box>
      <Box display={{ base: 'flex', md: 'none' }} overflow="hidden" width="100%" justifyContent="center">
        <Box width="85%" maxW="250px">
          <BookCardSkeleton />
        </Box>
      </Box>
    </Box>
  </VStack>
);

export const HorizontalBooks: React.FC<HorizontalBooksProps> = ({
  name,
  bestSellerName,
  hideIfEmpty,
  hookApi,
  description,
  showMore,
  params,
  receivedBook,
  borderColor,
  bg,
  showEditorialCode = true,
}) => {
  const { data, isLoading } = hookApi(params);

  let books = data?.results || [];

  if (params?.sortByName === 'true') {
    const order = [
      'Época Negócios',
      'Pequenas Empresas & Grandes Negócios',
      'Galileu',
      'Marie Claire',
      'Auto Esporte',
      'Globo Rural',
    ];
    const orderedBooks = order
      .map((name) => books.find((b: any) => b.name?.toLowerCase() === name.toLowerCase()))
      .filter(Boolean);
    const remainingBooks = books.filter(
      (b: any) => !order.some((name) => b.name?.toLowerCase() === name.toLowerCase())
    );
    books = [...orderedBooks, ...remainingBooks];
  }

  if (books.length === 0 && hideIfEmpty && !isLoading) {
    return null;
  }

  if (isLoading) {
    return <HorizontalBooksSkeleton name={name} description={description} borderColor={borderColor} bg={bg} />;
  }

  return (
    <SliderComponent size={books.length || 1} name={name} description={description} showMore={showMore} borderColor={borderColor} bg={bg}>
      {({ currentSlide }) =>
        books.map((book: any, index: number) => (
          <BookCard
            bestSellerName={bestSellerName}
            showBestSellersTag={book.isBestSeller}
            receivedBook={receivedBook}
            key={book.id}
            book={book}
            isActive={currentSlide === index}
            showEditorialCode={showEditorialCode}
          />
        ))
      }
    </SliderComponent>
  );
};
